import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMessage } from 'naive-ui'
import { useAppStore, useAuthStore, useClassroomStore, useFavoritesStore, useUserStore } from '@/store'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { getActiveMenuKey, getActiveMenuKeyClassroom } from '@/utils/routeHelper'
import { isUserRole } from '@/constants/role'

export function useNavigation() {
  const route = useRoute()
  const router = useRouter()
  const appStore = useAppStore()
  const { isMobile } = useBasicLayout()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const classroomStore = useClassroomStore()
  const message = useMessage()
  const userRole = computed(() => userStore.role)
  const slideUrl = import.meta.env.VITE_GLOB_SLIDE_URL

  const collapsed = computed(() => appStore.siderCollapsed)
  const isAdmin = isUserRole(userRole.value, 'ADMIN')
  const detailClassroom = computed(() => classroomStore.getCurrentClassroom)
  const classrooms = computed(() => classroomStore.getClassrooms)
  const favoritesStore = useFavoritesStore()
  const listSubjectPin = computed(() => favoritesStore.getFavorites)

  const keys: any[] = []
  if (listSubjectPin.value?.length) {
    listSubjectPin.value.forEach((folder: any) => {
      keys.push(`subject-${folder.id}`)
    })
  }

  const selectedKey = computed<string | null>(() => {
    return getActiveMenuKey(route)
  })

  const selectedKeyClassroom = computed<string | null>(() => {
    return getActiveMenuKeyClassroom(route, isAdmin, keys)
  })

  const safeAreaStyle = computed(() =>
    isMobile.value ? { paddingBottom: 'env(safe-area-inset-bottom)' } : {},
  )

  const getMobileClass = computed(() =>
    isMobile.value ? { position: 'fixed', zIndex: 50 } : {},
  )

  function handleUpdateCollapsed() {
    appStore.setSiderCollapsed(!collapsed.value)
  }

  function ensureClassroomId(): number | null {
    const current = detailClassroom.value
    if (current?.id)
      return current.id

    const fallback = Array.isArray(classrooms.value) && classrooms.value.length ? classrooms.value[0] : null
    if (fallback?.id) {
      classroomStore.setCurrentClassroom(fallback)
      return fallback.id
    }

    return null
  }

  // Mở slide trong tab mới với token bảo mật
  function openSlideInNewTab() {
    const baseUrl = `${slideUrl}/upload`
    const newTab = window.open(baseUrl, '_blank')

    // Gửi token sau khi tab mới load
    setTimeout(() => {
      if (newTab && !newTab.closed) {
        // Chỉ lấy các trường primitive để tránh lỗi serialization
        const userInfo = userStore.userInfo || {}
        const message = {
          sub: userInfo.sub,
          emailVerified: userInfo.emailVerified,
          email: userInfo.email,
          name: userInfo.name,
          phone: userInfo.phone,
          workPlace: userInfo.workPlace,
          experience: userInfo.experience,
          certificate: userInfo.certificate,
          userId: userInfo.userId,
          type: 'AUTH_TOKEN',
          token: authStore.token,
          theme: appStore.theme,
          timestamp: Date.now(),
        }

        // Gửi message đến tab mới
        newTab.postMessage(message, slideUrl)
      }
    }, 1000)
  }

  function handleSelect(key: string) {
    if (key.startsWith('folder-')) {
      const folderId = key.replace('folder-', '')
      router.push({ name: 'folder', params: { folderId } })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else if (key === 'workspace') {
      // Navigate to agent management when clicking workspace
      router.push({ name: 'dashboard' })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else if (key === 'agent-group' || key === 'community-group' || key === 'tool-group' || key === 'help-community' || key.startsWith('divider-') || key === 'more-tools') {
      // Don't navigate for group headers, dividers, more-tools popover, or parent menu items
    }
    else if (key === 'classroom-v2') {
      const route = router.resolve({
        name: key,
      })
      window.open(route.href, '_blank')
    }
    else if (key === 'slide') {
      // Mở slide trong tab mới với token bảo mật
      openSlideInNewTab()
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else if (key.startsWith('classroom-') && /^classroom-\d+$/.test(key)) {
      const classroomId = key.replace('classroom-', '')
      router.push({ name: 'classroom.teacher.detail', params: { classroomId } })
    }
    else if (key.startsWith('subject-') && /^subject-\d+$/.test(key)) {
      const subjectId = key.replace('subject-', '')
      router.push({ name: 'classroom.student.subject.detail', params: { classroomId: detailClassroom.value?.id, subjectId } })
    }
    else if (key === 'classroom.teacher.board') {
      const classroomId = ensureClassroomId()
      if (!classroomId) {
        message.error('Vui lòng chọn lớp học trước khi sử dụng tính năng này')
        return
      }
      router.push({ name: key, params: { classroomId } })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else if (key.startsWith('classroom.teacher.') || key.startsWith('classroom.student.')) {
      const classroomId = ensureClassroomId()
      if (!classroomId) {
        message.error('Vui lòng chọn lớp học trước khi sử dụng tính năng này')
        return
      }
      router.push({ name: key, params: { classroomId } })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else if (key.endsWith('-item')) {
      // Bỏ qua các menu item không phải là route (có suffix -item)

    }
    else if (key === 'notification-config') {
      router.push({ name: 'notification-config' })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
    else {
      router.push({ name: key })
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
    }
  }

  return {
    collapsed,
    selectedKey,
    selectedKeyClassroom,
    safeAreaStyle,
    getMobileClass,
    handleUpdateCollapsed,
    handleSelect,
    openSlideInNewTab,
  }
}
